import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { usePageSEO } from '../hooks/usePageSEO';
import { MarketingHeader, MarketingFooter } from './OtherPages';
import {
  ShieldCheck, Lock, ArrowRight, ArrowUpRight, BookOpen, Database, Cpu, Bot,
  Scale, Share2, Globe, Archive, UserCheck, Gavel, Baby, Mail, RefreshCw,
  MessageCircle, Link2, Check, Sparkles, FileText, Cloud, Trash2, Eye,
  Download, KeyRound, Fingerprint, Target, UserPlus, Users, CheckCircle2,
} from 'lucide-react';

/* ═══════════════════════════
   DATA
═══════════════════════════ */
const LAST_UPDATED = 'June 13, 2026';
const EFFECTIVE_DATE = 'June 13, 2026';

const SECTION_META = [
  { id: 'introduction', num: '1', title: 'Introduction', Icon: BookOpen },
  { id: 'data-we-collect', num: '2', title: 'Personal Data We Collect', Icon: Database },
  { id: 'how-we-use-data', num: '3', title: 'How We Use Your Data', Icon: Cpu },
  { id: 'ai-features', num: '4', title: 'AI Features & Your Data', Icon: Bot },
  { id: 'gdpr-basis', num: '5', title: 'Legal Basis (GDPR)', Icon: Scale },
  { id: 'data-sharing', num: '6', title: 'Data Sharing & Third Parties', Icon: Share2 },
  { id: 'international-transfers', num: '7', title: 'International Transfers', Icon: Globe },
  { id: 'data-retention', num: '8', title: 'Data Retention', Icon: Archive },
  { id: 'security', num: '9', title: 'Security', Icon: ShieldCheck },
  { id: 'your-rights', num: '10', title: 'Your Privacy Rights', Icon: UserCheck },
  { id: 'ccpa', num: '11', title: 'California Rights (CCPA)', Icon: Gavel },
  { id: 'childrens-privacy', num: '12', title: "Children's Privacy", Icon: Baby },
  { id: 'marketing', num: '13', title: 'Marketing Communications', Icon: Mail },
  { id: 'policy-changes', num: '14', title: 'Policy Changes', Icon: RefreshCw },
  { id: 'contact', num: '15', title: 'Contact & Data Protection', Icon: MessageCircle },
];

const QUICK_SUMMARY = [
  { Icon: Database, title: 'What We Collect', desc: 'Account details, resume content, and (with consent) anonymized usage data — only what powers your experience.', id: 'data-we-collect' },
  { Icon: Cpu, title: 'How We Use It', desc: "To build your documents, run AI features, process payments, and keep MarsResume secure and improving.", id: 'how-we-use-data' },
  { Icon: UserCheck, title: 'Your Rights', desc: 'Access, correct, export, or permanently delete your data — whenever you want, no questions asked.', id: 'your-rights' },
  { Icon: ShieldCheck, title: 'Security', desc: 'Bank-grade TLS encryption, hashed passwords, and strict access controls protect everything you store.', id: 'security' },
];

const TIMELINE = [
  { Icon: UserPlus, title: 'You Sign Up', desc: 'Create an account with your name, email, and a securely hashed password.' },
  { Icon: FileText, title: 'Resume Created', desc: 'Your experience, skills, and content are saved to your encrypted account.' },
  { Icon: Target, title: 'ATS Analysis', desc: 'Your resume is scored against real applicant-tracking-system criteria.' },
  { Icon: Bot, title: 'AI Processing', desc: "Content goes to Anthropic's Claude API only for your request — never to train models." },
  { Icon: Cloud, title: 'Cloud Storage', desc: 'Documents live in an encrypted, access-controlled MongoDB Atlas database.' },
  { Icon: Trash2, title: 'Delete Anytime', desc: 'Close your account and all personal data is erased within 30 days.' },
];

const SECURITY_CARDS = [
  { Icon: Lock, title: '256-bit SSL Encryption', desc: 'All data is transmitted using 256-bit TLS/HTTPS encryption in transit.' },
  { Icon: Scale, title: 'GDPR Compliant', desc: 'Full compliance with EU/UK data protection law and your GDPR rights.' },
  { Icon: Cloud, title: 'Secure Cloud Storage', desc: 'MongoDB Atlas with IP whitelisting and role-based access control.' },
  { Icon: KeyRound, title: 'Access Controls', desc: 'Role-based permissions strictly limit who can ever access your data.' },
  { Icon: Fingerprint, title: 'Password Encryption', desc: 'Passwords are hashed with bcrypt — never stored or recoverable in plaintext.' },
  { Icon: RefreshCw, title: 'Regular Security Reviews', desc: 'Periodic audits of our infrastructure, dependencies, and codebase.' },
];

const RIGHTS = [
  { Icon: Eye, title: 'Access Your Data', desc: 'Request a copy of all personal data we hold about you.', action: 'Email Privacy Team', href: 'mailto:privacy@marsresume.com' },
  { Icon: Download, title: 'Download Your Data', desc: 'Receive your data in a machine-readable, portable format.', action: 'Email Privacy Team', href: 'mailto:privacy@marsresume.com' },
  { Icon: UserCheck, title: 'Correct Information', desc: 'Update any inaccurate or incomplete data at any time.', action: 'Open Dashboard', path: '/dashboard' },
  { Icon: Trash2, title: 'Delete Your Account', desc: 'Permanently erase your account and personal data within 30 days.', action: 'Open Dashboard', path: '/dashboard' },
  { Icon: RefreshCw, title: 'Withdraw Consent', desc: 'Opt out of marketing communications or analytics whenever you choose.', action: 'Email Privacy Team', href: 'mailto:privacy@marsresume.com' },
  { Icon: MessageCircle, title: 'Contact Support', desc: 'Reach our privacy team with any question, big or small.', action: 'Contact Us', path: '/contact' },
];

const FAQS = [
  ['How is my data protected?', 'All data is encrypted in transit with 256-bit TLS/HTTPS, passwords are hashed with bcrypt, and our MongoDB Atlas database uses IP whitelisting and role-based access control. See Section 9 (Security) for the full list of safeguards we maintain.'],
  ['Does MarsResume use my resume to train AI?', "No. We never use your personal resume data to train AI models — neither our own nor any third party's. Your content is sent to Anthropic's Claude API only to fulfil the specific AI request you make, as explained in Section 4."],
  ['Can I delete my account?', 'Yes, at any time from your Profile settings in the dashboard. Once deleted, your personal data is permanently erased within 30 days, as described in Section 8 (Data Retention).'],
  ['How long is data stored?', 'Account and resume data is retained for the lifetime of your account. Payment records are kept for 7 years as required by financial regulations, and support communications for up to 2 years. Full details are in Section 8.'],
  ['Who can access my information?', 'Only what is necessary to run the Service: Stripe for payments, Google/Firebase Analytics for anonymized usage data (with consent), and Anthropic for AI requests you initiate. We never sell or rent your data — see Section 6.'],
  ['How do I contact privacy support?', 'Email privacy@marsresume.com for any privacy-related question or data request. We aim to reply within 24 hours, and formal rights requests are completed within 30 days as required by law — see Section 15.'],
];

/* ═══════════════════════════
   SHARED PIECES
═══════════════════════════ */
const SectionHeading = ({ title, subtitle }) => (
  <div className="pp-sec-head">
    <h2>{title}</h2>
    {subtitle && <p>{subtitle}</p>}
  </div>
);

/* ═══════════════════════════
   HERO
═══════════════════════════ */
const HeroSection = ({ onViewPolicy, onContactPrivacy }) => (
  <section className="pp-hero">
    <div className="pp-hero-grid-bg" />
    <div className="pp-hero-glow" />
    <div className="pp-hero-inner">
      <motion.div className="pp-hero-left" initial={{ opacity: 0, y: 26 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}>
        <h1><span className="pp-hero-highlight">Privacy</span> comes first.</h1>
        <p>Your trust matters. Learn how MarsResume securely collects, stores, and protects your personal information while helping you build better resumes.</p>

        <div className="pp-hero-meta">
          <div className="pp-hero-meta-item"><span>Last Updated</span><strong>{LAST_UPDATED}</strong></div>
          <div className="pp-hero-meta-item"><span>Effective Date</span><strong>{EFFECTIVE_DATE}</strong></div>
          <div className="pp-hero-meta-item pp-hero-meta-badge"><ShieldCheck size={14} /><strong>GDPR Ready</strong></div>
        </div>

        <div className="pp-hero-ctas">
          <motion.button whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }} type="button" className="pp-btn-primary" onClick={onViewPolicy}>
            View Policy <ArrowRight size={16} />
          </motion.button>
          <motion.button whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }} type="button" className="pp-btn-ghost-dark" onClick={onContactPrivacy}>
            Contact Privacy Team
          </motion.button>
        </div>
      </motion.div>

      <motion.div className="pp-hero-right" initial={{ opacity: 0, y: 26 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}>
        <div className="pp-illu-glow" />
        <motion.div className="pp-illu-shield" animate={{ y: [0, -12, 0] }} transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }}>
          <div className="pp-illu-shield-ring">
            <ShieldCheck size={40} />
          </div>
          <div className="pp-illu-shield-label">Data Encrypted</div>
          <div className="pp-illu-shield-sub"><span className="pp-illu-dot" /> Protected end-to-end</div>
        </motion.div>

        <motion.div className="pp-illu-float pp-illu-float-1" animate={{ y: [0, -10, 0] }} transition={{ duration: 4.5, repeat: Infinity, ease: 'easeInOut', delay: 0.3 }}>
          <Lock size={14} /> AES-256 Encrypted
        </motion.div>
        <motion.div className="pp-illu-float pp-illu-float-2" animate={{ y: [0, 10, 0] }} transition={{ duration: 5.2, repeat: Infinity, ease: 'easeInOut', delay: 0.6 }}>
          <Bot size={14} /> AI: Never trains on your data
        </motion.div>
        <motion.div className="pp-illu-float pp-illu-float-3" animate={{ y: [0, -8, 0] }} transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut', delay: 0.9 }}>
          <FileText size={14} /> Resume.pdf
        </motion.div>
      </motion.div>
    </div>
  </section>
);

/* ═══════════════════════════
   QUICK SUMMARY
═══════════════════════════ */
const QuickSummarySection = ({ onJump }) => (
  <section className="pp-section">
    <SectionHeading title="Your privacy, summarized" subtitle="A quick overview before you dive into the full policy below." />
    <div className="pp-summary-grid">
      {QUICK_SUMMARY.map((c, i) => (
        <motion.button key={c.title} type="button" className="pp-summary-card" onClick={() => onJump(c.id)}
          initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.4, delay: i * 0.06 }} whileHover={{ y: -6 }}>
          <motion.div className="pp-summary-icon" whileHover={{ rotate: 8 }}><c.Icon size={22} /></motion.div>
          <div className="pp-summary-title">{c.title}</div>
          <p className="pp-summary-desc">{c.desc}</p>
          <span className="pp-summary-arrow"><ArrowUpRight size={15} /></span>
        </motion.button>
      ))}
    </div>
  </section>
);

/* ═══════════════════════════
   PRIVACY TIMELINE
═══════════════════════════ */
const TimelineSection = () => (
  <section className="pp-section pp-section-tint">
    <SectionHeading title="Your data journey" subtitle="What happens to your information, step by step." />
    <div className="pp-timeline">
      {TIMELINE.map((step, i) => (
        <React.Fragment key={step.title}>
          <motion.div className="pp-timeline-card" initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.4 }} transition={{ duration: 0.4, delay: i * 0.07 }}>
            <div className="pp-timeline-icon"><step.Icon size={20} /></div>
            <div className="pp-timeline-title">{step.title}</div>
            <p className="pp-timeline-desc">{step.desc}</p>
          </motion.div>
          {i < TIMELINE.length - 1 && <div className="pp-timeline-arrow"><ArrowRight size={16} /></div>}
        </React.Fragment>
      ))}
    </div>
  </section>
);

/* ═══════════════════════════
   SECURITY
═══════════════════════════ */
const SecuritySection = () => (
  <section className="pp-section">
    <SectionHeading title="Security you can rely on" subtitle="The safeguards protecting your account and documents around the clock." />
    <div className="pp-security-grid">
      {SECURITY_CARDS.map((c, i) => (
        <motion.div key={c.title} className="pp-security-card" initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }} transition={{ duration: 0.4, delay: i * 0.06 }} whileHover={{ y: -5 }}>
          <div className="pp-security-icon"><c.Icon size={22} /></div>
          <div className="pp-security-title">{c.title}</div>
          <p className="pp-security-desc">{c.desc}</p>
        </motion.div>
      ))}
    </div>
  </section>
);

/* ═══════════════════════════
   YOUR RIGHTS
═══════════════════════════ */
const RightsSection = ({ navigate, goTo }) => (
  <section className="pp-section pp-section-tint">
    <SectionHeading title="Your privacy rights" subtitle="Exercise any of these rights at any time — no fees, no hassle." />
    <div className="pp-rights-grid">
      {RIGHTS.map((r, i) => (
        <motion.div key={r.title} className="pp-rights-card" initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }} transition={{ duration: 0.4, delay: i * 0.06 }}>
          <div className="pp-rights-icon"><r.Icon size={22} /></div>
          <div className="pp-rights-title">{r.title}</div>
          <p className="pp-rights-desc">{r.desc}</p>
          {r.href ? (
            <a href={r.href} className="pp-rights-btn">{r.action} <ArrowRight size={13} /></a>
          ) : (
            <button type="button" className="pp-rights-btn" onClick={() => r.path === '/contact' ? navigate('/contact') : goTo(r.path)}>{r.action} <ArrowRight size={13} /></button>
          )}
        </motion.div>
      ))}
    </div>
  </section>
);

/* ═══════════════════════════
   FAQ
═══════════════════════════ */
const FAQSection = () => {
  const [openIdx, setOpenIdx] = useState(null);
  return (
    <section className="pp-section">
      <SectionHeading title="Frequently Asked Questions" />
      <div className="pp-faq-list">
        {FAQS.map(([q, a], i) => {
          const isOpen = openIdx === i;
          return (
            <div key={q} className={`pp-faq-item${isOpen ? ' open' : ''}`}>
              <button type="button" className="pp-faq-trigger" aria-expanded={isOpen} onClick={() => setOpenIdx(isOpen ? null : i)}>
                <span>{q}</span>
                <span className="pp-faq-icon">
                  <span className="bar h" />
                  <motion.span className="bar v" animate={{ rotate: isOpen ? 90 : 0 }} transition={{ duration: 0.25, ease: 'easeInOut' }} />
                </span>
              </button>
              <AnimatePresence initial={false}>
                {isOpen && (
                  <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }} style={{ overflow: 'hidden' }}>
                    <div className="pp-faq-answer">{a}</div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}
      </div>
    </section>
  );
};

/* ═══════════════════════════
   CONTACT PRIVACY TEAM
═══════════════════════════ */
const ContactPrivacySection = ({ sectionRef, navigate }) => (
  <section className="pp-section pp-section-tint" ref={sectionRef}>
    <div className="pp-contact-card">
      <div className="pp-contact-icon"><MessageCircle size={26} /></div>
      <h2>Questions about your privacy?</h2>
      <p>If you have any questions regarding your personal information, our privacy team is here to help.</p>
      <div className="pp-contact-actions">
        <a href="mailto:privacy@marsresume.com" className="pp-btn-primary"><Mail size={16} /> Email Privacy Team</a>
        <button type="button" className="pp-btn-secondary" onClick={() => navigate('/contact')}>Contact Support</button>
      </div>
      <div className="pp-contact-meta">
        <div className="pp-contact-meta-item"><Mail size={14} /> support@marsresume.com</div>
        <div className="pp-contact-meta-divider" />
        <div className="pp-contact-meta-item"><CheckCircle2 size={14} /> Response time: within 24 hours</div>
      </div>
    </div>
  </section>
);

/* ═══════════════════════════
   FINAL TRUST SECTION
═══════════════════════════ */
const FinalTrustSection = ({ goTo }) => (
  <section className="pp-final-outer">
    <div className="pp-final-card">
      <div className="pp-final-mesh" />
      <div className="pp-final-glow" />
      <div className="pp-final-particle p1"><Lock size={14} /></div>
      <div className="pp-final-particle p2"><ShieldCheck size={12} /></div>
      <div className="pp-final-particle p3"><Lock size={10} /></div>
      <motion.div className="pp-final-content" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.4 }} transition={{ duration: 0.5 }}>
        <div className="pp-final-shield"><ShieldCheck size={28} /></div>
        <h2>Your privacy is our priority.</h2>
        <p>We build AI tools that respect your data, protect your information, and give you complete control over your privacy.</p>
        <motion.button whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }} type="button" className="pp-btn-primary" style={{ padding: '14px 30px', fontSize: 15 }} onClick={() => goTo('/signup')}>
          Create Resume Free <ArrowRight size={16} />
        </motion.button>
      </motion.div>
    </div>
  </section>
);

/* ═══════════════════════════
   POLICY SECTION CARD (legal content wrapper)
═══════════════════════════ */
const PrivacySection = React.forwardRef(({ meta, onCopy, copiedId, children }, ref) => {
  const { id, num, title, Icon } = meta;
  return (
    <div id={id} ref={ref} className="pp-card" data-section-id={id} style={{ scrollMarginTop: 110 }}>
      <div className="pp-card-head">
        <div className="pp-card-icon"><Icon size={19} /></div>
        <div className="pp-card-headtext">
          <span className="pp-card-num">Section {num}</span>
          <h2>{title}</h2>
        </div>
        <button type="button" className="pp-copy-btn" onClick={() => onCopy(id)} aria-label="Copy link to this section" title="Copy link to this section">
          {copiedId === id ? <Check size={15} /> : <Link2 size={15} />}
        </button>
      </div>
      <div className="pp-card-body">
        {children}
      </div>
    </div>
  );
});

/* ═══════════════════════════
   MAIN PAGE
═══════════════════════════ */
export const PrivacyPolicyPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const contactRef = useRef(null);
  const contentTopRef = useRef(null);
  const sectionRefs = useRef({});
  const [activeId, setActiveId] = useState(SECTION_META[0].id);
  const [copiedId, setCopiedId] = useState(null);

  usePageSEO({
    title: 'Privacy Policy – MarsResume',
    description: 'Learn how MarsResume collects, uses, and protects your personal data. We are committed to your privacy and data security.',
    canonical: '/privacy-policy',
  });

  const goTo = (path) => navigate(user ? (path === '/signup' ? '/dashboard' : path) : path);

  const scrollToId = useCallback((id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  const scrollToContact = () => contactRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  const scrollToContent = () => contentTopRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  const handleCopy = async (id) => {
    const url = `${window.location.origin}/privacy-policy#${id}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopiedId(id);
      setTimeout(() => setCopiedId(c => (c === id ? null : c)), 1600);
    } catch { /* clipboard unavailable — ignore */ }
  };

  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) setActiveId(entry.target.id);
      });
    }, { rootMargin: '-15% 0px -70% 0px', threshold: 0 });

    SECTION_META.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  return (
    <div className="pp-page">
      <PrivacyStyles />
      <MarketingHeader />

      <HeroSection onViewPolicy={scrollToContent} onContactPrivacy={scrollToContact} />
      <QuickSummarySection onJump={scrollToId} />

      {/* ── Sticky TOC + Policy Content ── */}
      <section className="pp-section" ref={contentTopRef}>
        <div className="pp-content-grid">
          <aside className="pp-toc">
            <div className="pp-toc-label">On this page</div>
            <nav className="pp-toc-list">
              {SECTION_META.map(({ id, num, title }) => (
                <button key={id} type="button" className={`pp-toc-link${activeId === id ? ' active' : ''}`} onClick={() => scrollToId(id)}>
                  <span className="pp-toc-num">{num}</span>
                  <span>{title}</span>
                </button>
              ))}
            </nav>
          </aside>

          <div className="pp-policy-col">
            <div className="pp-callout pp-callout-green">
              <strong>Our core promise:</strong> We never sell your personal data, never use your resume content to train AI models, and you can delete your account and all your data at any time.
            </div>

            <PrivacySection meta={SECTION_META[0]} onCopy={handleCopy} copiedId={copiedId}>
              <p className="pp-p"><strong>Devops Markaz Tech Pvt. Ltd.</strong> ("we", "us", or "our") is the data controller and operator of MarsResume ("the Service"). This Privacy Policy explains what personal data we collect, why we collect it, how we use it, who we share it with, and what rights you have regarding your information.</p>
              <p className="pp-p">By using MarsResume, you agree to the collection and use of your information in accordance with this policy. Please read it carefully alongside our <a href="/terms-of-use" className="pp-link">Terms of Use</a> and <a href="/cookie-policy" className="pp-link">Cookie Policy</a>.</p>
            </PrivacySection>

            <PrivacySection meta={SECTION_META[1]} onCopy={handleCopy} copiedId={copiedId}>
              <p className="pp-p"><strong>Information you provide directly:</strong></p>
              <ul className="pp-ul">
                <li><strong>Account data:</strong> Your full name, email address, and hashed password when you register via email</li>
                <li><strong>OAuth profile data:</strong> Your name, email, and profile photo when you sign in via Google or LinkedIn (we only request basic profile scopes)</li>
                <li><strong>Resume &amp; CV content:</strong> Work experience, education history, skills, certifications, projects, career goals, and any other personal or professional information you enter into your documents</li>
                <li><strong>Cover letter content:</strong> Professional summaries, job-specific letters, and related text</li>
                <li><strong>Payment data:</strong> Your billing details are collected and processed by Stripe. We receive only a payment confirmation token — we never store your full card number, CVV, or bank account details</li>
                <li><strong>Support communications:</strong> Messages you send us via contact forms or email</li>
                <li><strong>Profile preferences:</strong> Your profile photo, display name, and account settings</li>
              </ul>
              <p className="pp-p"><strong>Information collected automatically (when you accept analytics cookies):</strong></p>
              <ul className="pp-ul">
                <li><strong>Usage analytics:</strong> Pages visited, features used, button clicks, session duration, resume edits, and export actions — collected via Firebase Analytics (Google)</li>
                <li><strong>Approximate geographic location:</strong> Country, region, and city derived from your IP address by Firebase Analytics. This is used solely to understand where our users come from in aggregate. We do not store or process your precise GPS location</li>
                <li><strong>Device &amp; browser data:</strong> IP address, browser type and version, operating system, screen resolution, and referring URL</li>
                <li><strong>Authentication tokens:</strong> Secure JWT tokens stored in your browser's localStorage to maintain your login session</li>
                <li><strong>Cookies &amp; local storage:</strong> See our <a href="/cookie-policy" className="pp-link">Cookie Policy</a> for full details</li>
                <li><strong>Referral data:</strong> Referral codes used during signup and referral activity for our Referral Program</li>
              </ul>
            </PrivacySection>

            <PrivacySection meta={SECTION_META[2]} onCopy={handleCopy} copiedId={copiedId}>
              <p className="pp-p">We use your personal data for the following purposes:</p>
              <ul className="pp-ul">
                <li><strong>Service delivery:</strong> Creating and managing your account, storing your documents, and enabling all platform features</li>
                <li><strong>Payment processing:</strong> Handling subscription charges, renewals, and refunds via Stripe</li>
                <li><strong>AI features:</strong> Transmitting your resume content to Anthropic's Claude AI API to generate writing suggestions, improvements, ATS analysis, and interview questions</li>
                <li><strong>Communications:</strong> Sending account confirmation emails, billing receipts, password reset links, and product update notifications</li>
                <li><strong>Security &amp; fraud prevention:</strong> Detecting and preventing unauthorized access, fraudulent referrals, and abuse of the platform</li>
                <li><strong>Service improvement:</strong> Analyzing anonymized usage patterns to improve features, fix bugs, and enhance the user experience</li>
                <li><strong>Legal compliance:</strong> Meeting obligations under applicable laws, responding to legal requests, and protecting our rights</li>
                <li><strong>Referral program:</strong> Tracking referral codes, awarding points, and managing reward redemptions</li>
              </ul>
            </PrivacySection>

            <PrivacySection meta={SECTION_META[3]} onCopy={handleCopy} copiedId={copiedId}>
              <p className="pp-p">When you use AI-powered features (AI Resume Writer, AI Cover Letter Writer, AI Job Tailoring, ATS Checker, Interview Prep), your resume content is transmitted to <strong>Anthropic's Claude API</strong> to generate the requested output. We want you to understand exactly how this works:</p>
              <ul className="pp-ul">
                <li>Your content is sent to Anthropic <strong>only for the specific request</strong> you make — it is not stored on Anthropic's servers beyond completing the response</li>
                <li><strong>We do not use your personal resume data to train AI models</strong> — neither our own nor third-party models</li>
                <li>All data in transit to Anthropic is encrypted via TLS/HTTPS</li>
                <li>AI-generated output is suggestions only — you review and control all final content</li>
              </ul>
              <div className="pp-callout pp-callout-blue">
                <strong>Note:</strong> Anthropic's data handling is governed by their own <a href="https://www.anthropic.com/privacy" target="_blank" rel="noopener noreferrer" className="pp-link-blue">Privacy Policy</a>. We recommend reviewing it if you have specific concerns about AI data processing.
              </div>
            </PrivacySection>

            <PrivacySection meta={SECTION_META[4]} onCopy={handleCopy} copiedId={copiedId}>
              <p className="pp-p">If you are located in the European Economic Area (EEA), UK, or similar jurisdiction, our processing of your personal data is based on:</p>
              <ul className="pp-ul">
                <li><strong>Contract performance:</strong> Processing necessary to provide the Service you subscribed to (account management, document storage, payment processing)</li>
                <li><strong>Legitimate interests:</strong> Analytics, security monitoring, fraud prevention, and service improvement</li>
                <li><strong>Legal obligation:</strong> Compliance with applicable laws and regulatory requirements</li>
                <li><strong>Consent:</strong> Marketing emails (you may withdraw consent at any time)</li>
              </ul>
            </PrivacySection>

            <PrivacySection meta={SECTION_META[5]} onCopy={handleCopy} copiedId={copiedId}>
              <p className="pp-p"><strong>We never sell or rent your personal data.</strong> We share your information only in the following circumstances:</p>
              <ul className="pp-ul">
                <li><strong>Stripe</strong> — Payment processing. We share billing information necessary to complete transactions. Stripe is PCI-DSS Level 1 certified. See <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer" className="pp-link">Stripe Privacy Policy</a>.</li>
                <li><strong>Google / Firebase Analytics</strong> — When you accept analytics cookies, anonymized usage data (pages visited, feature interactions, and approximate geographic location derived from your IP address) is sent to Firebase Analytics, a Google product. This data is used solely to improve MarsResume. Google may process this data on servers located in the United States. See <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="pp-link">Google Privacy Policy</a>.</li>
                <li><strong>Google OAuth</strong> — When you choose to sign in with Google, we receive your name, email, and profile picture from Google's API with your explicit consent.</li>
                <li><strong>LinkedIn OAuth</strong> — When you choose to sign in with LinkedIn, we receive your name, email, and public profile data from LinkedIn's API with your explicit consent.</li>
                <li><strong>Anthropic</strong> — Your resume/document content is processed by Anthropic's API when you use AI features. This is limited to the specific AI request you initiate.</li>
                <li><strong>Legal requirements</strong> — We may disclose your information if required by law, court order, or government authority, or to protect MarsResume's legal rights.</li>
                <li><strong>Business transfers</strong> — In the event of a merger, acquisition, or sale of assets, your data may be transferred. We will notify you before such a transfer and your data will remain subject to this Privacy Policy.</li>
              </ul>
            </PrivacySection>

            <PrivacySection meta={SECTION_META[6]} onCopy={handleCopy} copiedId={copiedId}>
              <p className="pp-p">MarsResume is operated by a company based in <strong>Pakistan</strong>. Your data may be processed in Pakistan and potentially in other countries where our service providers operate (including the United States for Stripe and Anthropic). When transferring data internationally, we ensure appropriate safeguards are in place as required by applicable privacy laws.</p>
            </PrivacySection>

            <PrivacySection meta={SECTION_META[7]} onCopy={handleCopy} copiedId={copiedId}>
              <p className="pp-p">We retain your personal data for as long as your account is active or as needed to provide the Service:</p>
              <ul className="pp-ul">
                <li><strong>Account &amp; resume data:</strong> Retained for the lifetime of your account</li>
                <li><strong>After account deletion:</strong> Personal data is permanently deleted within <strong>30 days</strong></li>
                <li><strong>Payment records:</strong> Retained for 7 years as required by financial regulations</li>
                <li><strong>Support communications:</strong> Retained for up to 2 years</li>
                <li><strong>Anonymized analytics:</strong> May be retained indefinitely as they cannot identify individuals</li>
              </ul>
              <p className="pp-p">You can delete your account at any time from your Profile settings in the dashboard.</p>
            </PrivacySection>

            <PrivacySection meta={SECTION_META[8]} onCopy={handleCopy} copiedId={copiedId}>
              <p className="pp-p">We take data security seriously and implement the following measures:</p>
              <ul className="pp-ul">
                <li><strong>Encryption in transit:</strong> All data is transmitted via 256-bit TLS/HTTPS encryption</li>
                <li><strong>Password security:</strong> Passwords are hashed using bcrypt with salt rounds — we cannot recover your plaintext password</li>
                <li><strong>JWT authentication:</strong> Secure JSON Web Tokens with expiry are used for session management</li>
                <li><strong>Database security:</strong> MongoDB Atlas with IP whitelisting and role-based access control</li>
                <li><strong>Payment security:</strong> We never store payment card data — all payment handling is delegated to Stripe</li>
                <li><strong>Regular audits:</strong> Periodic security reviews of our infrastructure and codebase</li>
              </ul>
              <p className="pp-p">While we implement industry-standard safeguards, no online platform can guarantee 100% security. In the event of a data breach affecting your rights and freedoms, we will notify you and relevant authorities as required by law.</p>
            </PrivacySection>

            <PrivacySection meta={SECTION_META[9]} onCopy={handleCopy} copiedId={copiedId}>
              <p className="pp-p">Depending on your location, you have the following rights regarding your personal data:</p>
              <ul className="pp-ul">
                <li><strong>Right of access:</strong> Request a copy of the personal data we hold about you</li>
                <li><strong>Right to rectification:</strong> Request correction of inaccurate or incomplete data</li>
                <li><strong>Right to erasure ("right to be forgotten"):</strong> Request deletion of your personal data</li>
                <li><strong>Right to restriction:</strong> Request that we limit processing of your data in certain circumstances</li>
                <li><strong>Right to data portability:</strong> Receive your data in a machine-readable format</li>
                <li><strong>Right to object:</strong> Object to processing based on legitimate interests</li>
                <li><strong>Right to withdraw consent:</strong> Withdraw consent for marketing at any time</li>
              </ul>
              <p className="pp-p">To exercise any of these rights, contact us at <a href="mailto:privacy@marsresume.com" className="pp-link">privacy@marsresume.com</a>. We will respond within 30 days. You also have the right to lodge a complaint with your local data protection authority.</p>
            </PrivacySection>

            <PrivacySection meta={SECTION_META[10]} onCopy={handleCopy} copiedId={copiedId}>
              <p className="pp-p">If you are a California resident, you have additional rights under the California Consumer Privacy Act (CCPA):</p>
              <ul className="pp-ul">
                <li><strong>Right to know:</strong> What personal information we collect, use, disclose, or sell</li>
                <li><strong>Right to delete:</strong> Request deletion of personal information we have collected</li>
                <li><strong>Right to opt-out:</strong> We do not sell personal information — there is nothing to opt out of</li>
                <li><strong>Right to non-discrimination:</strong> You will not be discriminated against for exercising your CCPA rights</li>
              </ul>
              <p className="pp-p">To submit a CCPA request, contact us at <a href="mailto:privacy@marsresume.com" className="pp-link">privacy@marsresume.com</a>.</p>
            </PrivacySection>

            <PrivacySection meta={SECTION_META[11]} onCopy={handleCopy} copiedId={copiedId}>
              <p className="pp-p">MarsResume is not directed to individuals under the age of <strong>16</strong>. We do not knowingly collect personal information from children. If you believe a child has created an account or provided personal data, please contact us at <a href="mailto:privacy@marsresume.com" className="pp-link">privacy@marsresume.com</a> and we will promptly delete that information.</p>
            </PrivacySection>

            <PrivacySection meta={SECTION_META[12]} onCopy={handleCopy} copiedId={copiedId}>
              <p className="pp-p">We may send you product updates, new feature announcements, and promotional emails. You can unsubscribe at any time by clicking the "Unsubscribe" link at the bottom of any marketing email or by contacting us at <a href="mailto:support@marsresume.com" className="pp-link">support@marsresume.com</a>. Note: transactional emails (receipts, password resets, account alerts) cannot be opted out of as they are essential to the Service.</p>
            </PrivacySection>

            <PrivacySection meta={SECTION_META[13]} onCopy={handleCopy} copiedId={copiedId}>
              <p className="pp-p">We may update this Privacy Policy from time to time. When we make significant changes, we will notify you via email and/or a prominent notice in the dashboard. The "Last updated" date at the top of this page indicates when the policy was last revised. Your continued use of the Service after changes constitutes acceptance of the updated policy.</p>
            </PrivacySection>

            <PrivacySection meta={SECTION_META[14]} onCopy={handleCopy} copiedId={copiedId}>
              <p className="pp-p">For any privacy-related questions, data requests, or concerns, please contact us:</p>
              <ul className="pp-ul">
                <li><strong>Email:</strong> <a href="mailto:privacy@marsresume.com" className="pp-link">privacy@marsresume.com</a></li>
                <li><strong>General support:</strong> <a href="mailto:support@marsresume.com" className="pp-link">support@marsresume.com</a></li>
                <li><strong>Company:</strong> Devops Markaz Tech Pvt. Ltd.</li>
                <li><strong>Website:</strong> <a href="https://marsresume.com" className="pp-link">marsresume.com</a></li>
              </ul>
            </PrivacySection>

            <div className="pp-legal-nav">
              {[['Privacy Policy', '/privacy-policy'], ['Terms of Use', '/terms-of-use'], ['Cookie Policy', '/cookie-policy'], ['Contact Us', '/contact']].map(([label, path]) => (
                <button key={label} type="button" onClick={() => navigate(path)}>{label}</button>
              ))}
            </div>
          </div>
        </div>
      </section>

      <TimelineSection />
      <SecuritySection />
      <RightsSection navigate={navigate} goTo={goTo} />
      <FAQSection />
      <ContactPrivacySection sectionRef={contactRef} navigate={navigate} />
      <FinalTrustSection goTo={goTo} />

      <MarketingFooter navigate={navigate} goTo={goTo} />
    </div>
  );
};

/* ═══════════════════════════
   STYLES
═══════════════════════════ */
const PrivacyStyles = () => (
  <style>{`
    .pp-page { min-height: 100vh; background: #FFFFFF; font-family: 'Inter', sans-serif; color: #111827; overflow-x: clip; }

    .pp-btn-primary { background:#EF1D28;color:white;border:none;border-radius:12px;padding:13px 24px;font-weight:700;font-size:14px;cursor:pointer;font-family:inherit;display:inline-flex;align-items:center;gap:8px;transition:box-shadow 0.2s, background 0.2s; box-shadow:0 4px 16px rgba(239,29,40,0.25); text-decoration:none; }
    .pp-btn-primary:hover { background:#D91620; box-shadow:0 10px 26px rgba(239,29,40,0.35); }
    .pp-btn-ghost-dark { background: rgba(255,255,255,0.06); border: 1.5px solid rgba(255,255,255,0.2); color: white; border-radius: 12px; padding: 13px 24px; font-weight: 700; font-size: 14px; cursor: pointer; font-family: inherit; display: inline-flex; align-items: center; gap: 8px; transition: background 0.2s, border-color 0.2s; }
    .pp-btn-ghost-dark:hover { background: rgba(255,255,255,0.12); border-color: rgba(255,255,255,0.35); }
    .pp-btn-secondary { display: inline-flex; align-items: center; gap: 8px; background: white; border: 1.5px solid #E5E7EB; color: #374151; border-radius: 12px; padding: 13px 24px; font-weight: 700; font-size: 14px; cursor: pointer; font-family: inherit; transition: all 0.2s; }
    .pp-btn-secondary:hover { border-color: #D1D5DB; background: #F9FAFB; }

    .pp-link { color: #EF1D28; text-decoration: underline; text-decoration-color: transparent; transition: text-decoration-color 0.15s; }
    .pp-link:hover { text-decoration-color: #EF1D28; }
    .pp-link-blue { color: #1D4ED8; text-decoration: underline; text-decoration-color: transparent; transition: text-decoration-color 0.15s; }
    .pp-link-blue:hover { text-decoration-color: #1D4ED8; }

    /* Section shell */
    .pp-section { padding: 88px 48px; max-width: 1180px; margin: 0 auto; }
    .pp-section-tint { background: #FAFBFF; max-width: none; padding-left: 48px; padding-right: 48px; }
    .pp-section-tint > * { max-width: 1180px; margin-left: auto; margin-right: auto; }
    .pp-sec-head { text-align: center; margin-bottom: 48px; }
    .pp-sec-head h2 { margin: 0; font-size: 34px; font-weight: 900; color: #0F172A; letter-spacing: -0.8px; }
    .pp-sec-head p { margin: 12px auto 0; color: #64748B; font-size: 15px; line-height: 1.65; max-width: 560px; }

    /* Hero */
    .pp-hero { background: linear-gradient(160deg,#070E1A 0%,#0B1020 50%,#0A1420 100%); padding: 96px 48px 104px; position: relative; overflow: hidden; }
    .pp-hero-grid-bg { position: absolute; inset: 0; background-image: radial-gradient(rgba(255,255,255,0.06) 1px,transparent 1px); background-size: 28px 28px; pointer-events: none; }
    .pp-hero-glow { position: absolute; top: 5%; left: 10%; width: 720px; height: 440px; background: radial-gradient(ellipse, rgba(239,29,40,0.16) 0%, transparent 70%); pointer-events: none; }
    .pp-hero-inner { max-width: 1180px; margin: 0 auto; display: flex; align-items: center; gap: 60px; position: relative; }
    .pp-hero-left { flex: 0 0 50%; }
    .pp-hero-badge { display: inline-flex; align-items: center; gap: 7px; background: rgba(239,29,40,0.14); border: 1px solid rgba(239,29,40,0.25); border-radius: 24px; padding: 6px 16px; font-size: 11px; font-weight: 700; color: #FCA5A5; letter-spacing: 1px; text-transform: uppercase; margin-bottom: 22px; }
    .pp-hero-left h1 { margin: 0 0 20px; font-size: 48px; line-height: 1.12; font-weight: 900; letter-spacing: -1.4px; color: white; }
    .pp-hero-highlight { background: linear-gradient(135deg,#EF1D28,#FF6B6B); -webkit-background-clip: text; background-clip: text; -webkit-text-fill-color: transparent; }
    .pp-hero-left p { margin: 0 0 30px; font-size: 16.5px; line-height: 1.7; color: #94A3B8; max-width: 480px; }
    .pp-hero-meta { display: flex; align-items: center; gap: 22px; flex-wrap: wrap; margin-bottom: 34px; }
    .pp-hero-meta-item { display: flex; flex-direction: column; gap: 3px; }
    .pp-hero-meta-item span { font-size: 10.5px; color: #64748B; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px; }
    .pp-hero-meta-item strong { font-size: 14px; color: white; font-weight: 800; }
    .pp-hero-meta-badge { flex-direction: row; align-items: center; gap: 6px; background: rgba(34,197,94,0.12); border: 1px solid rgba(34,197,94,0.3); border-radius: 20px; padding: 7px 14px; color: #4ADE80; }
    .pp-hero-meta-badge strong { color: #4ADE80; font-size: 12.5px; }
    .pp-hero-ctas { display: flex; gap: 12px; flex-wrap: wrap; }
    .pp-hero-right { flex: 1; display: flex; align-items: center; justify-content: center; position: relative; min-height: 380px; }

    /* Hero illustration */
    .pp-illu-glow { position: absolute; inset: 0; background: radial-gradient(circle at 50% 50%, rgba(239,29,40,0.18) 0%, transparent 62%); pointer-events: none; }
    .pp-illu-shield { position: relative; width: 260px; background: rgba(255,255,255,0.98); backdrop-filter: blur(18px); border-radius: 24px; box-shadow: 0 40px 90px rgba(0,0,0,0.45); border: 1px solid rgba(255,255,255,0.1); padding: 32px 24px; text-align: center; }
    .pp-illu-shield-ring { width: 76px; height: 76px; border-radius: 50%; background: linear-gradient(135deg,#FFF1F2,#FFE4E6); color: #EF1D28; display: flex; align-items: center; justify-content: center; margin: 0 auto 16px; box-shadow: 0 0 0 8px rgba(239,29,40,0.06); }
    .pp-illu-shield-label { font-size: 15px; font-weight: 900; color: #111827; margin-bottom: 6px; }
    .pp-illu-shield-sub { display: flex; align-items: center; justify-content: center; gap: 6px; font-size: 11.5px; color: #059669; font-weight: 600; }
    .pp-illu-dot { width: 6px; height: 6px; border-radius: 50%; background: #22C55E; }
    .pp-illu-float { position: absolute; display: flex; align-items: center; gap: 8px; background: rgba(255,255,255,0.98); border-radius: 14px; padding: 10px 14px; font-size: 12px; font-weight: 800; color: #111827; box-shadow: 0 16px 40px rgba(0,0,0,0.3); white-space: nowrap; }
    .pp-illu-float-1 { top: -10px; right: -30px; color: #EF1D28; }
    .pp-illu-float-2 { bottom: 30px; left: -50px; color: #0891B2; }
    .pp-illu-float-3 { bottom: -14px; right: -10px; color: #7C3AED; }

    /* Quick summary */
    .pp-summary-grid { display: grid; grid-template-columns: repeat(4, minmax(0,1fr)); gap: 22px; }
    .pp-summary-card { position: relative; text-align: left; background: white; border: 1.5px solid #E5E7EB; border-radius: 24px; padding: 30px 22px; cursor: pointer; font-family: inherit; overflow: hidden; transition: box-shadow 0.25s ease, border-color 0.25s ease; }
    .pp-summary-card::before { content: ''; position: absolute; inset: 0; border-radius: 24px; padding: 1.5px; background: linear-gradient(135deg,#EF1D28,#FF6B6B); opacity: 0; -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0); -webkit-mask-composite: xor; mask-composite: exclude; transition: opacity 0.25s ease; pointer-events: none; }
    .pp-summary-card:hover { box-shadow: 0 22px 50px rgba(15,23,42,0.1); }
    .pp-summary-card:hover::before { opacity: 1; }
    .pp-summary-icon { width: 50px; height: 50px; border-radius: 14px; background: linear-gradient(135deg,#FFF1F2,#FFE4E6); color: #EF1D28; display: flex; align-items: center; justify-content: center; margin-bottom: 18px; }
    .pp-summary-title { font-size: 16px; font-weight: 800; color: #111827; margin-bottom: 8px; letter-spacing: -0.2px; }
    .pp-summary-desc { margin: 0 0 16px; font-size: 13px; color: #6B7280; line-height: 1.6; }
    .pp-summary-arrow { display: inline-flex; align-items: center; justify-content: center; width: 28px; height: 28px; border-radius: 50%; background: #F9FAFB; color: #9CA3AF; transition: background 0.2s, color 0.2s; }
    .pp-summary-card:hover .pp-summary-arrow { background: #FFF1F2; color: #EF1D28; }

    /* TOC + content grid */
    .pp-content-grid { display: grid; grid-template-columns: 280px 1fr; gap: 40px; align-items: start; }
    .pp-toc { position: sticky; top: 96px; background: white; border: 1px solid #E5E7EB; border-radius: 18px; padding: 20px; max-height: calc(100vh - 130px); overflow-y: auto; }
    .pp-toc-label { font-size: 11px; font-weight: 800; color: #9CA3AF; text-transform: uppercase; letter-spacing: 0.6px; margin-bottom: 12px; padding: 0 4px; }
    .pp-toc-list { display: flex; flex-direction: column; gap: 2px; }
    .pp-toc-link { display: flex; align-items: center; gap: 10px; width: 100%; background: none; border: none; border-left: 2px solid transparent; border-radius: 8px; padding: 8px 10px; font-size: 13px; font-weight: 600; color: #6B7280; cursor: pointer; font-family: inherit; text-align: left; transition: color 0.15s, background 0.15s, border-color 0.15s; }
    .pp-toc-link:hover { color: #111827; background: #F9FAFB; }
    .pp-toc-link.active { color: #EF1D28; background: #FFF1F2; border-left-color: #EF1D28; font-weight: 800; }
    .pp-toc-num { font-size: 11px; font-weight: 800; color: #D1D5DB; min-width: 16px; }
    .pp-toc-link.active .pp-toc-num { color: #EF1D28; }

    .pp-policy-col { display: flex; flex-direction: column; gap: 18px; min-width: 0; }
    .pp-callout { border-radius: 14px; padding: 16px 20px; font-size: 14px; line-height: 1.75; }
    .pp-callout-green { background: linear-gradient(135deg,#F0FDF4,#ECFDF5); border: 1px solid #BBF7D0; border-left: 4px solid #16A34A; color: #14532D; }
    .pp-callout-blue { background: linear-gradient(135deg,#EFF6FF,#F0F9FF); border: 1px solid #BFDBFE; border-left: 4px solid #3B82F6; color: #1E3A8A; margin: 14px 0 0; }
    .pp-callout-yellow { background: linear-gradient(135deg,#FFFBEB,#FFF7ED); border: 1px solid #FDE68A; border-left: 4px solid #F59E0B; color: #78350F; margin: 14px 0 0; }
    .pp-callout-red { background: linear-gradient(135deg,#FEF2F2,#FFF1F2); border: 1px solid #FECACA; border-left: 4px solid #EF4444; color: #7F1D1D; margin: 14px 0 0; }

    .pp-card { background: white; border: 1.5px solid #E5E7EB; border-radius: 24px; padding: 32px 36px; transition: border-color 0.2s, box-shadow 0.2s; }
    .pp-card:hover { border-color: #D1D5DB; box-shadow: 0 10px 34px rgba(15,23,42,0.05); }
    .pp-card-head { display: flex; align-items: flex-start; gap: 14px; margin-bottom: 18px; }
    .pp-card-icon { width: 42px; height: 42px; border-radius: 12px; background: linear-gradient(135deg,#FFF1F2,#FFE4E6); color: #EF1D28; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
    .pp-card-headtext { flex: 1; }
    .pp-card-num { display: block; font-size: 10.5px; font-weight: 800; color: #9CA3AF; text-transform: uppercase; letter-spacing: 0.6px; margin-bottom: 3px; }
    .pp-card-headtext h2 { margin: 0; font-size: 19px; font-weight: 800; color: #0F172A; letter-spacing: -0.3px; }
    .pp-copy-btn { width: 34px; height: 34px; border-radius: 10px; background: #F9FAFB; border: 1px solid #E5E7EB; color: #9CA3AF; display: flex; align-items: center; justify-content: center; cursor: pointer; flex-shrink: 0; transition: all 0.15s; }
    .pp-copy-btn:hover { background: #FFF1F2; border-color: #FECDD3; color: #EF1D28; }
    .pp-card-body { padding-left: 56px; }
    .pp-p { margin: 0 0 13px; font-size: 15.5px; color: #374151; line-height: 1.85; }
    .pp-p:last-child { margin-bottom: 0; }
    .pp-ul { padding-left: 20px; margin: 10px 0 16px; }
    .pp-ul li { margin-bottom: 10px; font-size: 15px; color: #374151; line-height: 1.8; }
    .pp-ul li:last-child { margin-bottom: 0; }

    .pp-legal-nav { display: flex; justify-content: center; gap: 20px; flex-wrap: wrap; padding-top: 12px; }
    .pp-legal-nav button { background: none; border: none; color: #9CA3AF; font-size: 13px; cursor: pointer; font-family: inherit; padding: 0; transition: color 0.15s; }
    .pp-legal-nav button:hover { color: #EF1D28; }

    /* Timeline */
    .pp-timeline { display: flex; align-items: stretch; justify-content: center; gap: 4px; flex-wrap: wrap; max-width: 1180px; margin: 0 auto; }
    .pp-timeline-card { flex: 1 1 150px; max-width: 168px; background: white; border: 1px solid #E5E7EB; border-radius: 18px; padding: 22px 16px; text-align: center; transition: box-shadow 0.24s ease, transform 0.24s ease, border-color 0.24s ease; }
    .pp-timeline-card:hover { box-shadow: 0 16px 40px rgba(15,23,42,0.08); transform: translateY(-4px); border-color: #FBD3D5; }
    .pp-timeline-icon { width: 42px; height: 42px; border-radius: 12px; background: linear-gradient(135deg,#EF1D28,#FF6B6B); color: white; display: flex; align-items: center; justify-content: center; margin: 0 auto 14px; box-shadow: 0 6px 16px rgba(239,29,40,0.25); }
    .pp-timeline-title { font-size: 13.5px; font-weight: 800; color: #111827; margin-bottom: 6px; }
    .pp-timeline-desc { margin: 0; font-size: 11.5px; color: #6B7280; line-height: 1.5; }
    .pp-timeline-arrow { display: flex; align-items: center; justify-content: center; width: 26px; color: #FBD3D5; flex-shrink: 0; }

    /* Security */
    .pp-security-grid { display: grid; grid-template-columns: repeat(3, minmax(0,1fr)); gap: 20px; }
    .pp-security-card { background: white; border: 1px solid #E5E7EB; border-radius: 20px; padding: 28px 24px; transition: box-shadow 0.24s ease, transform 0.24s ease, border-color 0.24s ease; }
    .pp-security-card:hover { box-shadow: 0 18px 44px rgba(15,23,42,0.08); transform: translateY(-4px); border-color: #D1D5DB; }
    .pp-security-icon { width: 48px; height: 48px; border-radius: 14px; background: #FFF1F2; color: #EF1D28; display: flex; align-items: center; justify-content: center; margin-bottom: 18px; }
    .pp-security-title { font-size: 15.5px; font-weight: 800; color: #111827; margin-bottom: 8px; }
    .pp-security-desc { margin: 0; font-size: 13.5px; color: #6B7280; line-height: 1.65; }

    /* Rights */
    .pp-rights-grid { display: grid; grid-template-columns: repeat(3, minmax(0,1fr)); gap: 20px; }
    .pp-rights-card { background: white; border: 1px solid #E5E7EB; border-radius: 20px; padding: 28px 24px; transition: box-shadow 0.24s ease, transform 0.24s ease, border-color 0.24s ease; }
    .pp-rights-card:hover { box-shadow: 0 18px 44px rgba(15,23,42,0.08); transform: translateY(-4px); border-color: #D1D5DB; }
    .pp-rights-icon { width: 48px; height: 48px; border-radius: 14px; background: #EFF6FF; color: #2563EB; display: flex; align-items: center; justify-content: center; margin-bottom: 18px; }
    .pp-rights-title { font-size: 15.5px; font-weight: 800; color: #111827; margin-bottom: 8px; }
    .pp-rights-desc { margin: 0 0 18px; font-size: 13.5px; color: #6B7280; line-height: 1.65; }
    .pp-rights-btn { display: inline-flex; align-items: center; gap: 6px; background: none; border: none; color: #EF1D28; font-size: 13px; font-weight: 800; cursor: pointer; font-family: inherit; padding: 0; text-decoration: none; }
    .pp-rights-btn:hover { text-decoration: underline; }

    /* FAQ */
    .pp-faq-list { display: flex; flex-direction: column; gap: 12px; max-width: 780px; margin: 0 auto; }
    .pp-faq-item { background: white; border: 1.5px solid #E5E7EB; border-radius: 16px; overflow: hidden; transition: border-color 0.25s ease, box-shadow 0.25s ease; }
    .pp-faq-item.open { border-color: #FECACA; box-shadow: 0 10px 30px rgba(239,29,40,0.08); }
    .pp-faq-trigger { width: 100%; display: flex; align-items: center; justify-content: space-between; gap: 16px; padding: 20px 24px; background: transparent; border: none; cursor: pointer; text-align: left; font-family: inherit; }
    .pp-faq-trigger span:first-child { font-size: 15.5px; font-weight: 700; color: #111827; line-height: 1.4; }
    .pp-faq-icon { position: relative; width: 30px; height: 30px; border-radius: 9px; background: #F9FAFB; border: 1px solid #E5E7EB; flex-shrink: 0; transition: background 0.25s ease, border-color 0.25s ease; }
    .pp-faq-item.open .pp-faq-icon { background: #FFF1F2; border-color: #FECDD3; }
    .pp-faq-icon .bar { position: absolute; top: 50%; left: 50%; background: #6B7280; border-radius: 2px; }
    .pp-faq-icon .bar.h { width: 12px; height: 2px; margin: -1px 0 0 -6px; }
    .pp-faq-icon .bar.v { width: 2px; height: 12px; margin: -6px 0 0 -1px; }
    .pp-faq-item.open .pp-faq-icon .bar { background: #EF1D28; }
    .pp-faq-answer { padding: 0 24px 22px; font-size: 14.5px; color: #6B7280; line-height: 1.8; }

    /* Contact privacy team */
    .pp-contact-card { max-width: 640px; margin: 0 auto; text-align: center; background: white; border: 1.5px solid #E5E7EB; border-radius: 24px; padding: 52px 44px; box-shadow: 0 20px 60px rgba(15,23,42,0.06); }
    .pp-contact-icon { width: 56px; height: 56px; border-radius: 16px; background: linear-gradient(135deg,#FFF1F2,#FFE4E6); color: #EF1D28; display: flex; align-items: center; justify-content: center; margin: 0 auto 22px; }
    .pp-contact-card h2 { margin: 0 0 12px; font-size: 28px; font-weight: 900; color: #0F172A; letter-spacing: -0.6px; }
    .pp-contact-card p { margin: 0 0 30px; font-size: 15px; color: #64748B; line-height: 1.7; }
    .pp-contact-actions { display: flex; gap: 12px; justify-content: center; flex-wrap: wrap; margin-bottom: 26px; }
    .pp-contact-meta { display: flex; align-items: center; justify-content: center; gap: 14px; flex-wrap: wrap; font-size: 12.5px; color: #6B7280; font-weight: 600; }
    .pp-contact-meta-item { display: flex; align-items: center; gap: 6px; }
    .pp-contact-meta-divider { width: 4px; height: 4px; border-radius: 50%; background: #D1D5DB; }

    /* Final trust */
    .pp-final-outer { padding: 20px 48px 96px; }
    .pp-final-card { max-width: 1100px; margin: 0 auto; background: linear-gradient(150deg, #060D1A 0%, #0B1020 50%, #0A1428 100%); border-radius: 28px; overflow: hidden; position: relative; padding: 76px 56px; border: 1px solid #1A2A3E; text-align: center; }
    .pp-final-mesh { position: absolute; inset: 0; background-image: radial-gradient(rgba(255,255,255,0.03) 1px, transparent 1px); background-size: 28px 28px; pointer-events: none; }
    .pp-final-glow { position: absolute; top: -100px; left: 50%; transform: translateX(-50%); width: 600px; height: 400px; background: radial-gradient(ellipse, rgba(239,29,40,0.18) 0%, transparent 65%); pointer-events: none; }
    .pp-final-particle { position: absolute; color: rgba(239,29,40,0.55); pointer-events: none; animation: ppParticleFloat 6s ease-in-out infinite; }
    .pp-final-particle.p1 { top: 20%; left: 12%; animation-delay: 0s; }
    .pp-final-particle.p2 { top: 62%; right: 15%; color: rgba(96,165,250,0.5); animation-delay: 1.4s; }
    .pp-final-particle.p3 { bottom: 18%; left: 24%; animation-delay: 2.6s; }
    @keyframes ppParticleFloat { 0%,100% { transform: translateY(0); opacity: 0.5; } 50% { transform: translateY(-18px); opacity: 1; } }
    .pp-final-content { position: relative; max-width: 560px; margin: 0 auto; }
    .pp-final-shield { width: 60px; height: 60px; border-radius: 18px; background: rgba(239,29,40,0.14); border: 1px solid rgba(239,29,40,0.3); color: #FCA5A5; display: flex; align-items: center; justify-content: center; margin: 0 auto 22px; }
    .pp-final-content h2 { margin: 0 0 14px; font-size: 34px; font-weight: 900; color: white; letter-spacing: -0.8px; line-height: 1.15; }
    .pp-final-content p { margin: 0 0 30px; color: #94A3B8; font-size: 15px; line-height: 1.6; }

    /* Responsive */
    @media (max-width: 1100px) {
      .pp-summary-grid { grid-template-columns: repeat(2, minmax(0,1fr)); }
      .pp-security-grid, .pp-rights-grid { grid-template-columns: repeat(2, minmax(0,1fr)); }
    }
    @media (max-width: 980px) {
      .pp-content-grid { grid-template-columns: 1fr; }
      .pp-toc { position: static; max-height: none; }
      .pp-toc-list { flex-direction: row; flex-wrap: wrap; gap: 6px; }
      .pp-toc-link { border-left: none; border: 1px solid #E5E7EB; }
      .pp-toc-link.active { border-color: #FECACA; }
    }
    @media (max-width: 900px) {
      .pp-hero { padding: 64px 24px 80px; }
      .pp-hero-inner { flex-direction: column; gap: 44px; text-align: center; }
      .pp-hero-left { flex: none; }
      .pp-hero-left p { margin-left: auto; margin-right: auto; }
      .pp-hero-meta { justify-content: center; }
      .pp-hero-ctas { justify-content: center; }
      .pp-hero-left h1 { font-size: 36px; }
      .pp-hero-right { min-height: 320px; margin-top: 12px; }
      .pp-section { padding: 56px 24px; }
      .pp-section-tint { padding-left: 24px; padding-right: 24px; }
      .pp-card { padding: 26px 22px; }
      .pp-card-body { padding-left: 0; margin-top: 4px; }
      .pp-timeline { flex-direction: column; align-items: stretch; }
      .pp-timeline-card { max-width: none; }
      .pp-timeline-arrow { transform: rotate(90deg); margin: -4px auto; }
      .pp-final-outer { padding: 12px 24px 72px; }
      .pp-final-card { padding: 48px 28px; }
    }
    @media (max-width: 640px) {
      .pp-summary-grid { grid-template-columns: 1fr; }
      .pp-security-grid, .pp-rights-grid { grid-template-columns: 1fr; }
      .pp-hero-left h1 { font-size: 30px; letter-spacing: -1px; }
      .pp-final-content h2 { font-size: 26px; }
      .pp-illu-float { display: none; }
      .pp-illu-shield { width: 100%; max-width: 260px; }
      .pp-contact-card { padding: 40px 24px; }
    }
  `}</style>
);
